<template>
  <div class="game-screen" @click="nextTask">
    <back-button @click="goBack" />

    <app-header
      :title="selectedMode ? selectedMode.title : 'Spiel'"
      :subtitle="isGameComplete ? 'Spiel beendet!' : `Runde ${currentRound}`"
      class="compact-header"
    />

    <div v-if="isLoading" class="loading-container">
      <div class="spinner"></div>
      <p>Lade Aufgaben...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <p>{{ error }}</p>
      <app-button @click="loadTasks">Erneut versuchen</app-button>
    </div>

    <div v-else-if="isGameComplete" class="game-complete">
      <div class="icon-container">
        <i class="fas fa-trophy"></i>
      </div>
      <p>Alle Aufgaben wurden gespielt!</p>
      <app-button @click="restartGame">Spiel neu starten</app-button>
    </div>

    <div v-else-if="currentPlayer && currentTask" class="task-container">
      <!-- Aktive Regeln und Viren -->
      <div
        v-if="activeRules.length > 0 || activeViruses.length > 0"
        class="active-status"
      >
        <div v-if="activeRules.length > 0" class="active-rules">
          <div class="active-header">
            <i class="fas fa-gavel"></i> Aktive Regeln
          </div>
          <div v-for="rule in activeRules" :key="rule.id" class="active-item">
            <span class="active-text">{{ rule.text }}</span>
          </div>
        </div>

        <div v-if="activeViruses.length > 0" class="active-viruses">
          <div class="active-header">
            <i class="fas fa-virus"></i> Aktive Viren
          </div>
          <div
            v-for="virus in activeViruses"
            :key="virus.id"
            class="active-item"
          >
            <span class="active-player">{{ virus.player.name }}:</span>
            <span class="active-text">{{ virus.text }}</span>
          </div>
        </div>
      </div>

      <div class="centered-content">
        <!-- Auflösungsansicht -->
        <div v-if="dissolveMode" class="dissolve-view">
          <div class="dissolve-icon">
            <i
              :class="
                dissolveInfo.type === 'rule' ? 'fas fa-gavel' : 'fas fa-virus'
              "
              :style="{
                color: dissolveInfo.type === 'rule' ? '#ffcc00' : '#ff0000',
              }"
            ></i>
          </div>
          <div class="dissolve-title">
            {{
              dissolveInfo.type === "rule"
                ? "Regel aufgehoben!"
                : "Virus besiegt!"
            }}
          </div>

          <div class="dissolve-message">
            <template v-if="dissolveInfo.type === 'rule'">
              <p>Die folgende Regel gilt nicht mehr:</p>
              <p class="dissolved-text">{{ dissolveInfo.rule.text }}</p>
            </template>
            <template v-else>
              <p>
                <strong>{{ dissolveInfo.virus.player.name }}</strong> ist
                befreit von:
              </p>
              <p class="dissolved-text">{{ dissolveInfo.virus.text }}</p>
            </template>
          </div>

          <div class="tap-to-continue">Tippe, um fortzufahren</div>
        </div>

        <!-- Normale Aufgabenansicht (nur anzeigen, wenn nicht im Auflösungsmodus) -->
        <template v-else>
          <div class="player-info" v-if="shouldShowPlayer">
            <div class="player-avatar">
              <i
                :class="
                  currentPlayer.gender === 'male'
                    ? 'fas fa-user'
                    : 'fas fa-user-alt'
                "
              ></i>
            </div>
            <div class="player-name">{{ currentPlayer.name }}</div>
          </div>
          <!-- Platzhalter, um die gleiche Höhe zu gewährleisten -->
          <div v-else class="player-info-placeholder"></div>

          <div class="task-bubble" :class="currentTask.type">
            <div class="task-type" :class="currentTask.type">
              <i :class="getTaskIcon(currentTask.type)"></i>
              {{ getTaskTypeName(currentTask.type) }}
            </div>
            <div class="task-text">{{ getModifiedTaskText(currentTask) }}</div>

            <!-- Timer für Zeitaufgaben -->
            <div
              v-if="currentTask.type === 'timed' && timerActive"
              class="timer-container"
            >
              <div
                class="timer-progress"
                :style="{ width: `${(timerRemaining / timerDuration) * 100}%` }"
              ></div>
              <div class="timer-text">{{ formatTime(timerRemaining) }}</div>
              <button
                v-if="!timerStarted"
                @click.stop="startTimer"
                class="timer-button"
              >
                <i class="fas fa-play"></i> Timer starten
              </button>
            </div>

            <!-- Quiz-Antwort -->
            <div v-if="currentTask.type === 'quiz'" class="quiz-container">
              <button @click.stop="toggleQuizAnswer" class="quiz-button">
                <i
                  :class="
                    quizAnswerRevealed ? 'fas fa-eye-slash' : 'fas fa-eye'
                  "
                ></i>
                {{
                  quizAnswerRevealed ? "Antwort verbergen" : "Antwort zeigen"
                }}
              </button>
              <div v-if="quizAnswerRevealed" class="quiz-answer">
                <strong>Antwort:</strong> {{ currentTask.answer }}
              </div>
            </div>

            <div class="difficulty">
              <span
                v-for="i in parseInt(currentTask.difficulty)"
                :key="i"
                class="difficulty-dot"
              ></span>
            </div>
          </div>
        </template>
      </div>

      <div class="action-buttons" v-if="!dissolveMode">
        <div class="tap-hint">
          <i class="fas fa-hand-pointer"></i>
          <span>Tippe für die nächste Aufgabe</span>
        </div>

        <div class="history-toggle" @click.stop="toggleHistory">
          <span>{{
            showHistory ? "Verlauf ausblenden" : "Verlauf anzeigen"
          }}</span>
          <i
            :class="showHistory ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
          ></i>
        </div>
      </div>

      <transition name="slide">
        <game-history
          v-if="gameHistory.length > 0 && showHistory && !dissolveMode"
          :history="gameHistory"
          :current-index="gameHistory.length - 1"
          class="history-panel"
        />
      </transition>
    </div>

    <div v-else class="start-game">
      <div class="player-list-game">
        <player-card
          v-for="(player, index) in players"
          :key="index"
          :player="player"
        />
      </div>

      <app-button @click="startGame" class="pulse"> Spiel starten </app-button>
    </div>

    <div class="game-stats" v-if="currentPlayer && currentTask">
      <div class="stats-item">
        <i class="fas fa-list-check"></i>
        <span>{{ remainingTasks }} Aufgaben übrig</span>
      </div>
      <div class="stats-item">
        <i class="fas fa-history"></i>
        <span>Runde {{ currentRound }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import BackButton from "@/components/UI/BackButton.vue";
import AppHeader from "@/components/UI/AppHeader.vue";
import AppButton from "@/components/UI/AppButton.vue";
import PlayerCard from "@/components/PlayerCard.vue";
import GameHistory from "@/components/GameHistory.vue";

export default {
  name: "GameScreen",
  components: {
    BackButton,
    AppHeader,
    AppButton,
    PlayerCard,
    GameHistory,
  },
  data() {
    return {
      taskTypes: {
        single: { name: "Einzelaufgabe", icon: "fas fa-user" },
        group: { name: "Gruppenaufgabe", icon: "fas fa-users" },
        virus: { name: "Virus", icon: "fas fa-virus" },
        rule: { name: "Regel", icon: "fas fa-gavel" },
        truth: { name: "Wahrheit", icon: "fas fa-comment-dots" },
        game: { name: "Spiel", icon: "fas fa-gamepad" },
        challenge: { name: "Challenge", icon: "fas fa-flag-checkered" },
        rhyme: { name: "Reime", icon: "fas fa-music" },
        category: { name: "Kategorie", icon: "fas fa-list" },
        timed: { name: "Zeitaufgabe", icon: "fas fa-stopwatch" },
        quiz: { name: "Quiz", icon: "fas fa-question" },
      },
      showHistory: false,
      timerStarted: false,
      timerInterval: null,
      dissolvedRules: [],
      dissolvedViruses: [],
    };
  },
  computed: {
    ...mapGetters({
      selectedMode: "getSelectedMode",
      players: "getPlayers",
      currentPlayer: "getCurrentPlayer",
      currentTask: "getCurrentTask",
      remainingTasks: "getRemainingTasks",
      isGameComplete: "isGameComplete",
      gameHistory: "getGameHistory",
      shouldShowPlayer: "shouldShowPlayer",
      activeViruses: "getActiveViruses",
      activeRules: "getActiveRules",
      currentRound: "getCurrentRound",
      timerActive: "getTimerActive",
      timerDuration: "getTimerDuration",
      timerRemaining: "getTimerRemaining",
      quizAnswerRevealed: "getQuizAnswerRevealed",
      dissolveMode: "getDissolveMode",
      dissolveInfo: "getDissolveInfo",
    }),
    isLoading() {
      return this.$store.state.isLoading;
    },
    error() {
      return this.$store.state.error;
    },
  },
  methods: {
    ...mapActions([
      "loadTasks",
      "startGameRound",
      "selectRandomPlayerAndTask",
      "toggleQuizAnswer",
      "resetTimer",
      "checkForExpiredRulesAndViruses",
      "continueAfterDissolve",
    ]),
    goBack() {
      this.resetTimer();
      clearInterval(this.timerInterval);
      this.$store.commit("resetGame");
      this.$router.push("/player-setup");
    },
    async startGame() {
      try {
        await this.loadTasks();
        if (!this.error) {
          this.startGameRound();
        }
      } catch (error) {
        console.error("Fehler beim Starten des Spiels:", error);
      }
    },
    nextTask(event) {
      console.log("new");
      // Verhindern, dass Klicks auf Buttons oder andere interaktive Elemente das Spiel fortsetzen
      if (
        event.target.closest("button") ||
        event.target.closest(".game-history") ||
        event.target.closest(".history-toggle") ||
        event.target.closest(".quiz-container") ||
        event.target.closest(".timer-container")
      ) {
        console.log("nop");
        return;
      }

      if (this.isGameComplete) {
        console.log("nop2");
        return;
      }

      // Bei aktiven Zeitaufgaben nichts tun
      if (
        this.currentTask &&
        this.currentTask.type === "timed" &&
        this.timerActive &&
        this.timerStarted
      ) {
        console.log("nop3");
        return;
      }

      if (this.dissolveMode) {
        // Wir sind im Auflösungsmodus - zur nächsten Aufgabe wechseln
        this.continueAfterDissolve();
        // Historie schließen
        console.log("nop4");
        this.showHistory = false;
        return;
      }

      if (this.currentPlayer && this.currentTask) {
        // Timer zurücksetzen, wenn aktiv

        console.log("yes");
        if (this.timerActive) {
          this.resetTimer();
          clearInterval(this.timerInterval);
          this.timerStarted = false;
          console.log("yes1");
        }

        console.log("yes2");
        // Prüfen, ob Regeln oder Viren abgelaufen sind
        const hasDissolve = this.checkForExpiredRulesAndViruses();
        console.log("yes3");

        // Wenn nichts aufgelöst wurde, nächste Aufgabe anzeigen
        if (hasDissolve) {
          console.log("yes4");
          this.selectRandomPlayerAndTask();
          console.log("yes5");
        }

        // Historie bei jedem Task-Wechsel schließen für bessere Übersicht
        this.showHistory = false;
      }
    },
    getTaskIcon(type) {
      return this.taskTypes[type]?.icon || "fas fa-question";
    },
    getTaskTypeName(type) {
      return this.taskTypes[type]?.name || "Aufgabe";
    },
    getModifiedTaskText(task) {
      // Ersetze "für die nächsten X Runden" mit "bis wir das Gegenteil sagen"
      if (task.type === "virus" || task.type === "rule") {
        let text = task.text;
        // Entferne Zahlenangaben zu Runden, wenn vorhanden
        text = text.replace(
          /für die nächsten \d+ Runden/gi,
          "bis wir das Gegenteil sagen"
        );
        text = text.replace(/für \d+ Runden/gi, "bis wir das Gegenteil sagen");
        text = text.replace(/\d+ Runden/gi, "bis wir das Gegenteil sagen");
        // Prüfe, ob "bis" bereits in der Aufgabe enthalten ist
        if (!text.includes("bis wir das Gegenteil sagen")) {
          // Prüfe, ob ein Punkt am Ende ist
          if (text.endsWith(".")) {
            // Füge vor dem letzten Punkt ein
            text = text.slice(0, -1) + " bis wir das Gegenteil sagen.";
          } else {
            // Füge am Ende hinzu
            text += " bis wir das Gegenteil sagen.";
          }
        }
        return text;
      }
      return task.text;
    },
    restartGame() {
      this.resetTimer();
      clearInterval(this.timerInterval);
      this.$store.commit("resetGame");
      this.startGame();
    },
    toggleHistory(event) {
      // Verhindern, dass der Click-Event an das parent-Element weitergegeben wird
      event.stopPropagation();
      this.showHistory = !this.showHistory;
    },
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    },
    startTimer(event) {
      if (event) {
        event.stopPropagation();
      }

      this.timerStarted = true;

      this.timerInterval = setInterval(() => {
        if (this.timerRemaining > 0) {
          this.$store.commit("updateTimer", this.timerRemaining - 1);
        } else {
          clearInterval(this.timerInterval);
          this.timerStarted = false;
        }
      }, 1000);
    },
  },
  watch: {
    // Überprüfen, ob eine neue Zeitaufgabe aktiv ist und automatisch starten
    currentTask(newTask) {
      if (newTask && newTask.type === "timed" && this.timerActive) {
        // Timer noch nicht direkt starten, sondern warten bis der Benutzer klickt
        this.timerStarted = false;
      }
    },
  },
  mounted() {
    // Verhindern von Scroll und Bounce-Effekten auf mobilen Geräten
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.height = "100%";

    // Laden der Aufgaben, wenn die Komponente geladen wird
    this.loadTasks().catch((error) => {
      console.error("Fehler beim Laden der Aufgaben:", error);
    });
  },
  beforeUnmount() {
    // Timer bereinigen
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    // Scroll-Verhalten wiederherstellen, wenn die Komponente zerstört wird
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.width = "";
    document.body.style.height = "";
  },
};
</script>

<style scoped>
.game-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding-top: var(--spacing-md);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
}

.compact-header {
  padding-top: var(--spacing-xs);
  padding-bottom: var(--spacing-xs);
  margin-bottom: 0;
}

.player-list-game {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: var(--spacing-sm);
  padding: 0 var(--spacing-sm);
  margin: var(--spacing-md) 0;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--secondary-bg);
  border-top: 4px solid var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: var(--spacing-md);
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-container {
  text-align: center;
  padding: var(--spacing-md) 0;
  color: var(--error-color);
}

.task-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  animation: fadeIn var(--transition-normal);
  overflow: hidden;
  padding: 0 var(--spacing-sm);
  position: relative;
}

.centered-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
}

.player-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: var(--spacing-sm);
  min-height: 80px; /* Feste Höhe für Spieler-Info */
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.player-info-placeholder {
  min-height: 80px; /* Gleiche Höhe wie player-info */
}

.player-avatar {
  font-size: 2.5rem;
  color: var(--accent-color);
  margin-bottom: var(--spacing-xs);
}

.player-name {
  font-size: var(--font-size-lg);
  font-weight: 600;
}

.task-bubble {
  background-color: var(--card-bg);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-md);
  width: 100%;
  max-width: 500px;
  box-shadow: var(--shadow-sm);
  position: relative;
  margin: 0;
  border-left: 4px solid transparent;
}

/* Farbige Ränder für verschiedene Aufgabentypen */
.task-bubble.single {
  border-left-color: #00c2ff;
}

.task-bubble.group {
  border-left-color: #4caf50;
}

.task-bubble.virus {
  border-left-color: #ff0000;
}

.task-bubble.rule {
  border-left-color: #ffcc00;
}

.task-bubble.truth {
  border-left-color: #b942ff;
}

.task-bubble.game {
  border-left-color: #ff5c8d;
}

.task-bubble.challenge {
  border-left-color: #ff9500;
}

.task-bubble.rhyme {
  border-left-color: #2196f3;
}

.task-bubble.category {
  border-left-color: #8bc34a;
}

.task-bubble.timed {
  border-left-color: #f44336;
}

.task-bubble.quiz {
  border-left-color: #9c27b0;
}

.task-type {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: var(--border-radius-sm);
  margin-bottom: var(--spacing-xs);
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--bg-color);
}

.task-type i {
  margin-right: 5px;
}

.task-type.single {
  background-color: #00c2ff;
}

.task-type.group {
  background-color: #4caf50;
}

.task-type.virus {
  background-color: #ff0000;
}

.task-type.rule {
  background-color: #ffcc00;
}

.task-type.truth {
  background-color: #b942ff;
}

.task-type.game {
  background-color: #ff5c8d;
}

.task-type.challenge {
  background-color: #ff9500;
}

.task-type.rhyme {
  background-color: #2196f3;
}

.task-type.category {
  background-color: #8bc34a;
}

.task-type.timed {
  background-color: #f44336;
}

.task-type.quiz {
  background-color: #9c27b0;
}

.task-text {
  font-size: var(--font-size-md);
  line-height: 1.3;
  margin-bottom: var(--spacing-sm);
}

.difficulty {
  display: flex;
  gap: 5px;
}

.difficulty-dot {
  width: 6px;
  height: 6px;
  background-color: var(--accent-color);
  border-radius: 50%;
  opacity: 0.8;
}

.action-buttons {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 500px;
  margin: var(--spacing-sm) 0;
}

.tap-hint {
  text-align: center;
  color: #8a8a8a;
  font-size: var(--font-size-xs);
  animation: pulse 2s infinite;
}

.tap-hint i {
  margin-right: 5px;
}

.history-toggle {
  color: var(--accent-color);
  cursor: pointer;
  padding: 6px 12px;
  border-radius: var(--border-radius-sm);
  background-color: rgba(0, 224, 208, 0.1);
  display: flex;
  align-items: center;
  font-size: var(--font-size-xs);
  font-weight: 500;
  z-index: 10;
}

.history-toggle i {
  margin-left: 5px;
  transition: transform var(--transition-fast);
}

.history-toggle:hover {
  background-color: rgba(0, 224, 208, 0.2);
}

.history-panel {
  width: 100%;
  max-width: 500px;
  margin-top: var(--spacing-xs);
  max-height: 30vh;
  overflow-y: auto;
  background-color: var(--secondary-bg);
  border-radius: var(--border-radius-md);
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 30vh;
  margin-top: var(--spacing-xs);
  opacity: 1;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
  overflow: hidden;
}

.game-stats {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: var(--secondary-bg);
  padding: 8px var(--spacing-md);
  display: flex;
  justify-content: space-between;
  z-index: 5;
}

.stats-item {
  display: flex;
  align-items: center;
  font-size: var(--font-size-xs);
}

.stats-item i {
  margin-right: 5px;
  color: var(--accent-color);
}

.game-complete {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
}

.icon-container {
  font-size: 3rem;
  color: var(--accent-color);
  margin-bottom: var(--spacing-md);
}

.game-complete p {
  font-size: var(--font-size-lg);
  margin-bottom: var(--spacing-md);
}

.start-game {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
}

/* Aktive Regeln und Viren */
.active-status {
  width: 100%;
  max-width: 500px;
  margin-bottom: var(--spacing-sm);
  font-size: var(--font-size-xs);
  background-color: var(--secondary-bg);
  border-radius: var(--border-radius-md);
  overflow: hidden;
}

.active-header {
  background-color: var(--card-bg);
  padding: 6px 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.active-header i {
  margin-right: 5px;
  color: var(--accent-color);
}

.active-item {
  padding: 6px 10px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.active-item:last-child {
  border-bottom: none;
}

.active-player {
  font-weight: 600;
  margin-right: 5px;
  color: var(--accent-color);
}

.active-text {
  flex: 1;
  min-width: 0;
}

/* Timer für Zeitaufgaben */
.timer-container {
  margin: var(--spacing-sm) 0;
  background-color: var(--secondary-bg);
  border-radius: var(--border-radius-sm);
  height: 90px;
  position: relative;
  overflow: hidden;
}

.timer-progress {
  height: 30px;
  background-color: var(--accent-color);
  transition: width 1s linear;
}

.timer-text {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.timer-button {
  margin-top: var(--spacing-xs);
  width: 100%;
  margin-top: 10px;
  background-color: var(--accent-color);
  color: var(--bg-color);
  border: none;
  border-radius: var(--border-radius-sm);
  padding: 0;
  line-height: 50px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.timer-button i {
  margin-right: 5px;
}

/* Quiz-Antwort */
.quiz-container {
  margin: var(--spacing-sm) 0;
}

.quiz-button {
  width: 100%;
  background-color: transparent;
  color: var(--accent-color);
  border: 1px solid var(--accent-color);
  border-radius: var(--border-radius-sm);
  padding: 8px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quiz-button i {
  margin-right: 5px;
}

.quiz-answer {
  margin-top: var(--spacing-xs);
  padding: var(--spacing-xs);
  background-color: rgba(0, 224, 208, 0.1);
  border-radius: var(--border-radius-sm);
  animation: fadeIn 0.3s ease;
}

/* Auflösungsbenachrichtigung */
/* Fügen Sie diese Styles zu Ihrem style-Block hinzu */

/* Auflösungsansicht */
.dissolve-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: var(--card-bg);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
  width: 100%;
  max-width: 500px;
  animation: fadeIn 0.5s ease, pulse 2s infinite;
}

.dissolve-icon {
  font-size: 3rem;
  margin-bottom: var(--spacing-md);
}

.dissolve-title {
  font-size: var(--font-size-xl);
  font-weight: 700;
  margin-bottom: var(--spacing-md);
}

.dissolve-message {
  margin-bottom: var(--spacing-lg);
}

.dissolved-text {
  font-weight: 600;
  font-style: italic;
  margin-top: var(--spacing-sm);
  padding: var(--spacing-sm);
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: var(--border-radius-sm);
}

.tap-to-continue {
  font-size: var(--font-size-sm);
  color: var(--accent-color);
  margin-top: var(--spacing-md);
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px dashed var(--accent-color);
  border-radius: var(--border-radius-sm);
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.02);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
